import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SettingsLayout } from "@/pages/SettingsBranding";
import { sanitizeHtml } from "@/lib/safeHtml";
import { toast } from "sonner";

export default function SettingsConsentDocs() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/consent-documents/manage"); setDocs(data); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const newVersion = async (type) => {
    const existing = docs.filter((d) => d.type === type);
    const last = existing[0];
    setEditing({ id: null, type, title: last?.title || `${type} (new version)`, body_html: last?.body_html || "" });
  };

  const saveNew = async () => {
    try {
      await api.post("/consent-documents", {
        type: editing.type,
        title: editing.title,
        body_html: editing.body_html,
      });
      toast.success("New version published");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SettingsLayout>
      <div data-testid="consent-docs-page">
        <p className="text-sm text-navy-500 mb-4">Consent documents are versioned. Publishing a new version automatically deactivates the old one and re-prompts visitors next time they sign in.</p>
        {err && <div className="text-sm text-danger mb-3">{err}</div>}

        {editing ? (
          <div className="eb-card p-6 space-y-3" data-testid="consent-edit-card">
            <div className="text-xs uppercase tracking-wider text-orange-600">{editing.type} — new version</div>
            <input className="eb-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="consent-edit-title" />
            <textarea className="eb-input min-h-[200px] font-mono text-xs" value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} data-testid="consent-edit-body" />
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-accent" onClick={saveNew} data-testid="consent-edit-save">Publish new version</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {loading ? <p className="text-navy-500">Loading…</p> : docs.map((d) => (
              <div key={d.id} className="eb-card p-5" data-testid={`consent-${d.type}-${d.version}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-orange-600">{d.type} · v{d.version}</div>
                    <div className="font-semibold">{d.title}</div>
                  </div>
                  <div className="flex gap-2">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${d.active ? "bg-success/15 text-success" : "bg-navy-100 text-navy-500"}`}>{d.active ? "Active" : "Archived"}</span>
                    {d.active && (
                      <button className="btn-outline text-xs" onClick={() => newVersion(d.type)} data-testid={`new-version-${d.type}`}>
                        New version
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 text-sm text-navy-700 prose max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(d.body_html) }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </SettingsLayout>
  );
}
